import { action, computed, makeObservable } from 'mobx';

import type { RootStore } from 'widget/stores/RootStore';
import {
  IWidgetCustomization,
  IWidgetSubmitStatus,
  WidgetConsentSubmitStatus,
  WidgetContactFormFieldType,
  WidgetContactFormMode,
  WidgetConsentsType
} from 'widget/entities/config';
import type {
  ISubmissionFormState,
  FormContactField,
  FormConsentField
} from 'widget/types/submissionFormState';
import FormFieldModel from 'widget/models/FormFieldModel';

import { ContactFormStore } from './ContactFormStore';
import { ConsentsStore } from './ConsentsStore';

type PrivateFields = '_isContactFieldsValid' | '_isConsentsValid';

export class SubmissionStore implements ISubmissionFormState {
  private _rootStore: RootStore;

  readonly contactFormStore: ContactFormStore;
  readonly consentsStore: ConsentsStore;
  readonly mode: FormFieldModel<WidgetContactFormMode | null>;

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;
    this.contactFormStore = new ContactFormStore(rootStore);
    this.consentsStore = new ConsentsStore(rootStore);
    this.mode = new FormFieldModel<WidgetContactFormMode | null>(null);

    makeObservable<SubmissionStore, PrivateFields>(this, {
      contactFormStore: false,
      consentsStore: false,

      _isContactFieldsValid: computed,
      _isConsentsValid: computed,

      isSubmitting: computed,
      isFormSubmissionComplete: computed,
      isSubmitted: computed,
      isFormValid: computed,
      isInputDisabled: computed,

      contactFormMode: computed,
      hasUserSentFirstMessage: computed,
      shouldShowConsents: computed,
      shouldShowContactForm: computed,
      shouldHideContent: computed,
      shouldShowConsentsInFooter: computed,
      isSendButtonDisabled: computed,
      canSendWithoutMessage: computed,

      contactFields: computed,
      enabledConsents: computed,

      init: action
    });
  }

  get shouldHideContent(): boolean {
    if (!this.mode.value) {
      return false;
    }

    return (
      this.mode.value === WidgetContactFormMode.beforeDialog &&
      (this.shouldShowContactForm || this.shouldShowConsents)
    );
  }

  get shouldShowConsentsInFooter(): boolean {
    if (
      this.consentsStore.consentsFields.length === 0 ||
      this.consentsStore.isDone ||
      this.consentsStore.submitStage.isLoading ||
      !this.mode.value
    ) {
      return false;
    }

    return this.mode.value === WidgetContactFormMode.disabled;
  }

  get isSendButtonDisabled(): boolean {
    if (this.isSubmitting) {
      return true;
    }

    if (this.shouldShowContactForm || this.shouldShowConsents) {
      return !this.isFormSubmissionComplete;
    }

    if (this.shouldShowConsentsInFooter) {
      return !this._isConsentsValid;
    }

    return false;
  }

  get canSendWithoutMessage(): boolean {
    return this.shouldShowConsentsInFooter && this._isConsentsValid;
  }

  get contactFormMode(): WidgetContactFormMode {
    return (
      this._rootStore.configStore.customizationConfig?.contactForm?.mode ??
      WidgetContactFormMode.disabled
    );
  }

  get shouldShowContactForm(): boolean {
    if (
      this.contactFormStore.contactFields.length === 0 ||
      this.contactFormStore.isDone ||
      this.contactFormStore.submitStage.isLoading ||
      !this.mode.value
    ) {
      return false;
    }

    switch (this.mode.value) {
      case WidgetContactFormMode.beforeDialog:
        return true;

      case WidgetContactFormMode.duringDialog:
        return this.hasUserSentFirstMessage;

      case WidgetContactFormMode.disabled:
        return false;

      default:
        return false;
    }
  }

  get shouldShowConsents(): boolean {
    if (
      this.consentsStore.consentsFields.length === 0 ||
      this.consentsStore.isDone ||
      !this.mode.value
    ) {
      return false;
    }

    switch (this.mode.value) {
      case WidgetContactFormMode.beforeDialog:
        return !this.isFormSubmissionComplete;

      case WidgetContactFormMode.duringDialog:
        return this.hasUserSentFirstMessage && !this.isFormSubmissionComplete;

      case WidgetContactFormMode.disabled:
        return false;

      default:
        return false;
    }
  }

  get isFormValid(): boolean {
    return this._isContactFieldsValid && this._isConsentsValid;
  }

  get isSubmitted(): boolean {
    return this.isFormSubmissionComplete;
  }

  get isSubmitting(): boolean {
    return (
      this.contactFormStore.submitStage.isLoading ||
      this.consentsStore.submitStage.isLoading
    );
  }

  get isInputDisabled(): boolean {
    return this.isSubmitting;
  }

  get contactFields(): ReadonlyArray<FormContactField> {
    return this.contactFormStore.contactFields.map((field) => ({
      type: field.type,
      required: field.required
    }));
  }

  get enabledConsents(): ReadonlyArray<FormConsentField> {
    const customization = this._rootStore.configStore.customizationConfig;
    const result: FormConsentField[] = [];

    const personalData = this.consentsStore.personalDataConsent;
    if (personalData) {
      result.push({
        type: WidgetConsentsType.personalData,
        required: personalData.isRequired,
        docUrl: customization?.consents.personalData.docUrl ?? null
      });
    }

    const mailing = this.consentsStore.mailingConsent;
    if (mailing) {
      result.push({
        type: WidgetConsentsType.mailing,
        required: mailing.isRequired,
        docUrl: customization?.consents.mailing.docUrl ?? null
      });
    }

    return result;
  }

  get submitStatuses(): IWidgetSubmitStatus {
    return {
      contactForm: this.contactFormStore.contactFormStatus.value,
      personalDataConsent:
        this.consentsStore.personalDataConsent?.status.value ??
        WidgetConsentSubmitStatus.pending,
      mailingConsent:
        this.consentsStore.mailingConsent?.status.value ??
        WidgetConsentSubmitStatus.pending
    };
  }

  get isFormSubmissionComplete(): boolean {
    return this.contactFormStore.isDone && this.consentsStore.isDone;
  }

  private get _isContactFieldsValid(): boolean {
    if (this.contactFormStore.isDone) {
      return true;
    }

    for (const field of this.contactFormStore.contactFields) {
      if (field.required && !field.value.trim()) {
        return false;
      }
      if (field.value.trim() && !field.isValid) {
        return false;
      }
    }

    return true;
  }

  private get _isConsentsValid(): boolean {
    if (this.consentsStore.isDone) {
      return true;
    }

    for (const consent of this.consentsStore.consentsFields) {
      if (consent.isRequired && !consent.enabled.value) {
        return false;
      }
    }

    return true;
  }

  get hasUserSentFirstMessage(): boolean {
    return this._rootStore.messagesStore.messages.some(
      (message) => !message.isBot
    );
  }

  getFieldValue = (type: WidgetContactFormFieldType): string => {
    const field = this.contactFormStore.contacts[type];
    return field?.value ?? '';
  };

  getFieldError = (type: WidgetContactFormFieldType): string | null => {
    const field = this.contactFormStore.contacts[type];
    return field?.error ?? null;
  };

  setFieldValue = (type: WidgetContactFormFieldType, value: string): void => {
    const field = this.contactFormStore.contacts[type];
    if (field) {
      field.formField.changeValue(value);
    }
  };

  setFieldBlur = (type: WidgetContactFormFieldType): void => {
    const field = this.contactFormStore.contacts[type];
    if (field) {
      field.formField.setTouched(true);
    }
  };

  getConsentValue = (type: WidgetConsentsType): boolean => {
    const consent = this.consentsStore.getConsent(type);
    return consent?.enabled.value ?? false;
  };

  setConsentValue = (type: WidgetConsentsType, checked: boolean): void => {
    const consent = this.consentsStore.getConsent(type);
    if (consent) {
      consent.enabled.changeValue(checked);
    }
  };

  submit = async (): Promise<{ isError: boolean }> => {
    try {
      const [contactResult, consentsResult] = await Promise.all([
        this.contactFormStore.isDone
          ? { isError: false }
          : this.contactFormStore.submitContacts(),
        this.consentsStore.isDone
          ? { isError: false }
          : this.consentsStore.submitConsents()
      ]);

      return { isError: contactResult.isError || consentsResult.isError };
    } catch {
      return { isError: true };
    }
  };

  onBeforeSend = (): void => {
    if (!this.shouldShowConsentsInFooter) {
      return;
    }

    if (!this.isFormSubmissionComplete && this.isFormValid) {
      this.submit();
    }
  };

  init(
    customization: IWidgetCustomization | null,
    statuses: IWidgetSubmitStatus | null
  ): { isError: boolean } {
    if (!customization || !statuses) {
      return { isError: true };
    }

    const { contactForm, consents } = customization;

    this.mode.changeValue(contactForm.mode);

    this.contactFormStore.init({
      fields: contactForm.fields,
      status: statuses.contactForm
    });

    this.consentsStore.init(consents, statuses);

    return { isError: false };
  }
}
